import * as React from 'react';
import {
    ContextActionType,
    ModalContext,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FilterDatatable,
    FilterOptionValue,
    FiltersDatatableList,
} from '@europrocurement/flexy-datatable';
import { useDispatch } from 'react-redux';

import { PackageJsonldPackageRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { UnknownAction } from 'redux';
import { addItem, FaOptionIcon } from '@europrocurement/l2d-icons';
import { useOffreService } from '@europrocurement/l2d-domain';
import { useDefaultContextActions } from '@europrocurement/l2d-modules/hooks';
import { Typography } from '@mui/material';
import ListingWithSearchAndPreview from '../components/ListingWithSearchAndPreview';
import Form from './Form/Form';
import { displayCustomValue, getStringColumn } from '../listings';
import { PossibleTypes } from '../../Societes';
import useFilterOptions from '../Catalog/useFilterOptions';

const Panel: React.FunctionComponent = function () {
    const { modalActions } = React.useContext(ModalContext);

    const dispatch = useDispatch();

    const { offerPackageModel: model } = useOffreService();
    const { backContextActions } = useDefaultContextActions();
    const filterOptions = useFilterOptions();

    const reloadList = () =>
        dispatch(model.datasourcesThunks.main.getData({}) as unknown as UnknownAction);

    const listingButton = {
        label: 'Créer un nouveau package',
        icon: addItem,
        action: () =>
            modalActions.call(
                <Form
                    isCreate
                    afterSubmit={reloadList}
                />,
            ),
    };

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer un nouveau package',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: listingButton.action,
            },
            ...backContextActions,
        ],
        [backContextActions, listingButton.action],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<PackageJsonldPackageRead>[] = [
        {
            ...getStringColumn('id'),
            sortable: true,
        },
        {
            ...getStringColumn('libelle', 'Libellé', 50),
            sortable: true,
        },
        {
            ...getStringColumn('nomCommercial', undefined, 25),
            sortable: true,
        },
        {
            ...displayCustomValue(
                'formalite' as keyof PackageJsonldPackageRead['formalite'],
                (formality: PackageJsonldPackageRead['formalite']) => formality?.libelle ?? '---',
                'Type de formalité',
                25,
            ),
            sortable: true,
        },
        {
            label: 'Domaine',
            displayLabel: true,
            render: (item) => {
                const domain = item.domaine as { libelle?: string };
                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {domain?.libelle ?? '(aucun domaine)'}
                    </Typography>
                );
            },
            isDisplayed: true,
            sortable: false,
            field: 'domaine',
        },
    ];

    /** Generic function : Generate filter object */
    const getFilter = (
        label: string,
        field: string,
        options: FilterOptionValue[],
    ): FilterDatatable => ({
        label,
        field,
        type: 'staticSelect' as const,
        options,
        renderFilterList: (value: string) => {
            const selectedOption = options.find((option) => option.value === value);
            return selectedOption ? selectedOption.label : 'Aucun';
        },
    });

    const filters: FiltersDatatableList = [
        {
            field: 'libelle',
            label: 'Libelle',
            type: 'text',
        },
        {
            field: 'nomCommercial',
            label: 'Nom commercial',
            type: 'text',
        },
        {
            field: 'formaliteLibelle',
            label: 'Type de formalité',
            type: 'text',
        },
        getFilter('Domaine', 'domaineId', filterOptions.domains),
    ];

    return (
        <ListingWithSearchAndPreview
            listingTitle="Liste des packages"
            listingButton={listingButton}
            listingColumns={columns}
            form={Form}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Package}
        />
    );
};

export default Panel;
